import React from "react";

type ToastType = "success" | "error" | "warning" | "message" | "info";
type ToastPayload = { type: ToastType; title: string; description?: string; id: string };

const EVENT_NAME = "psiconorte:safe-toast";

function emit(type: ToastType, title: unknown, options?: { description?: unknown }) {
  const text = typeof title === "string" ? title : title == null ? "" : String(title);
  const description = options?.description == null ? undefined : String(options.description);
  const payload: ToastPayload = {
    type,
    title: text,
    description,
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  };

  // Lovable/Safari sandbox-safe: no Notification API, no permissions, no service worker.
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent<ToastPayload>(EVENT_NAME, { detail: payload }));
    }
  } catch {
    // never break clinical workflows because a notification failed
  }

  const log = type === "error" ? console.error : type === "warning" ? console.warn : console.info;
  try { log(`[${type}] ${payload.title}${description ? ` — ${description}` : ""}`); } catch {}
  return payload.id;
}

export const toast = {
  success: (title: unknown, options?: { description?: unknown }) => emit("success", title, options),
  error: (title: unknown, options?: { description?: unknown }) => emit("error", title, options),
  warning: (title: unknown, options?: { description?: unknown }) => emit("warning", title, options),
  message: (title: unknown, options?: { description?: unknown }) => emit("message", title, options),
  info: (title: unknown, options?: { description?: unknown }) => emit("info", title, options),
};

function tone(type: ToastType) {
  if (type === "error") return "border-red-500/50 bg-red-950/90 text-red-50";
  if (type === "warning") return "border-amber-500/50 bg-amber-950/90 text-amber-50";
  if (type === "success") return "border-emerald-500/50 bg-emerald-950/90 text-emerald-50";
  return "border-sky-500/50 bg-slate-950/90 text-slate-50";
}

export function SafeToastViewport() {
  const [items, setItems] = React.useState<ToastPayload[]>([]);

  React.useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<ToastPayload>).detail;
      if (!detail?.title) return;
      setItems((prev) => [detail, ...prev].slice(0, 4));
      window.setTimeout(() => {
        setItems((prev) => prev.filter((x) => x.id !== detail.id));
      }, detail.type === "error" ? 5200 : 3200);
    };
    window.addEventListener(EVENT_NAME, handler);
    return () => window.removeEventListener(EVENT_NAME, handler);
  }, []);

  if (!items.length) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[9999] flex w-[min(420px,calc(100vw-2rem))] flex-col gap-2 pointer-events-none">
      {items.map((item) => (
        <div key={item.id} className={`pointer-events-auto rounded-xl border px-4 py-3 shadow-2xl backdrop-blur ${tone(item.type)}`}>
          <div className="text-sm font-semibold">{item.title}</div>
          {item.description ? <div className="mt-1 text-xs opacity-85">{item.description}</div> : null}
        </div>
      ))}
    </div>
  );
}
